import { useState, useEffect } from 'react';
import { Header, Hero, CommunityCards, Features, CTA, Footer } from './components';
import { ManosRibbon, ManosBand, ManosModal } from './components/manos';
import { useGeolocation } from './hooks/useGeolocation';
import { useCommunities } from './hooks/useCommunities';
import { useManosMarketing } from './hooks/useManosMarketing';
import { useSessionFlag } from './hooks/useSessionFlag';
import { getTranslations } from './i18n';
import { initFirebase, events } from './lib/firebase';
import type { Language, Community } from './types';

// Use relative paths for same-domain navigation (unified deploy)
// Works on both staging (int-enzo.quul.app) and production (quul.app)

// Maintenance mode - controlled by environment variable
const isMaintenanceMode = import.meta.env.VITE_MAINTENANCE_MODE === 'true';

function App() {
  const [language, setLanguage] = useState<Language>('es');
  const location = useGeolocation();
  const { communities } = useCommunities();
  const t = getTranslations(language);

  // Manos F7 marketing treatment — gated by `manos.marketing` (read via the public CF,
  // ADR-059-addendum-003; fail-closed). The interstitial modal shows once per session.
  const { enabled: manosEnabled } = useManosMarketing();
  const [manosModalSeen, markManosModalSeen] = useSessionFlag('manos_marketing_modal_seen');
  const showManosModal = manosEnabled && !manosModalSeen;

  // Initialize Firebase Analytics
  useEffect(() => {
    initFirebase();
    events.pageView('Landing Page', language);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Only run on mount - language tracking handled separately

  const handleToggleLanguage = () => {
    const newLang = language === 'en' ? 'es' : 'en';
    events.languageToggle(language, newLang);
    setLanguage(newLang);
  };

  const handleGetStarted = () => {
    if (isMaintenanceMode) return;
    events.ctaClick('get_started', 'header');
    window.location.href = '/register';
  };

  const handleSignIn = () => {
    if (isMaintenanceMode) return;
    events.ctaClick('sign_in', 'header');
    window.location.href = '/login';
  };

  const handleCommunityClick = (community: Community) => {
    if (community.status === 'active') {
      // Go directly to app feed
      window.location.href = '/feed';
    } else {
      // Go to waitlist in app
      events.waitlistClick(community.id);
      window.location.href = `/waitlist?community=${community.id}`;
    }
  };

  return (
    <div className="min-h-screen bg-white max-w-full overflow-x-hidden">
      {manosEnabled && <ManosRibbon t={t} />}
      <Header
        t={t}
        language={language}
        onToggleLanguage={handleToggleLanguage}
        onGetStarted={handleGetStarted}
        onSignIn={handleSignIn}
        isMaintenanceMode={isMaintenanceMode}
        manosEnabled={manosEnabled}
      />
      {manosEnabled && <ManosBand t={t} />}
      <Hero
        t={t}
        language={language}
        location={location}
        onGetStarted={handleGetStarted}
        isMaintenanceMode={isMaintenanceMode}
      />
      <CommunityCards
        t={t}
        communities={communities}
        onCommunityClick={handleCommunityClick}
      />
      <Features t={t} />
      <CTA t={t} onGetStarted={handleGetStarted} isMaintenanceMode={isMaintenanceMode} />
      <Footer t={t} />
      {showManosModal && <ManosModal t={t} onClose={markManosModalSeen} />}
    </div>
  );
}

export default App;
